import { useCallback, useState } from 'react'
import { Upload, Music, AlertCircle } from 'lucide-react'
import { formatFileSize } from '../utils/scoring'

interface DropZoneProps {
  onFileSelect: (file: File) => void
  isLoading?: boolean
  maxSizeMb?: number
}

const ACCEPTED_TYPES = ['audio/mpeg', 'audio/wav', 'audio/mp3', 'audio/x-wav', 'audio/wave']
const ACCEPTED_EXTENSIONS = ['.mp3', '.wav', '.wave']

export function DropZone({ onFileSelect, isLoading = false, maxSizeMb = 50 }: DropZoneProps) {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const validateFile = useCallback(
    (file: File): string | null => {
      const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))
      const isValidType =
        ACCEPTED_TYPES.includes(file.type) || ACCEPTED_EXTENSIONS.includes(extension)

      if (!isValidType) {
        return 'Please upload an MP3 or WAV file'
      }

      const maxBytes = maxSizeMb * 1024 * 1024
      if (file.size > maxBytes) {
        return `File size must be under ${maxSizeMb}MB (yours is ${formatFileSize(file.size)})`
      }

      return null
    },
    [maxSizeMb]
  )

  const handleFile = useCallback(
    (file: File) => {
      const validationError = validateFile(file)
      if (validationError) {
        setError(validationError)
        return
      }

      setError(null)
      onFileSelect(file)
    },
    [validateFile, onFileSelect]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)

      const file = e.dataTransfer.files[0]
      if (file) {
        handleFile(file)
      }
    },
    [handleFile]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      if (file) {
        handleFile(file)
      }
    },
    [handleFile]
  )

  return (
    <div className="w-full max-w-xl mx-auto">
      <label
        className={`
          drop-zone relative flex flex-col items-center justify-center
          w-full h-64 cursor-pointer
          ${isDragging ? 'dragging' : ''}
          ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <input
          type="file"
          className="hidden"
          accept=".mp3,.wav,audio/mpeg,audio/wav"
          onChange={handleInputChange}
          disabled={isLoading}
        />

        {/* Subtle grid background */}
        <div className="absolute inset-0 forge-grid-bg opacity-30 rounded-[18px]" />

        <div className="relative flex flex-col items-center gap-4 p-6 text-center z-10">
          {/* Icon with glow */}
          <div className="relative">
            {isDragging && (
              <div className="absolute inset-0 bg-[hsl(var(--forge-copper))] blur-2xl opacity-40 scale-150" />
            )}
            <div
              className={`
                relative p-5 rounded-2xl transition-all duration-300
                ${isDragging
                  ? 'bg-gradient-to-br from-[hsl(var(--forge-copper))] to-[hsl(var(--forge-copper-dim))] scale-110'
                  : 'bg-forge-surface'
                }
              `}
            >
              {isDragging ? (
                <Music className="w-8 h-8 text-[hsl(var(--forge-bg-deep))]" />
              ) : (
                <Upload className="w-8 h-8 text-forge-muted" />
              )}
            </div>
          </div>

          <div>
            <p className={`text-lg font-semibold transition-colors ${isDragging ? 'text-forge-copper' : 'text-white'}`}>
              {isDragging ? 'Drop to analyze' : 'Drop your audio file here'}
            </p>
            <p className="mt-1.5 text-sm text-forge-muted">
              or <span className="text-forge-copper hover:underline">browse files</span>
            </p>
          </div>

          <div className="flex items-center gap-3 text-xs text-forge-subtle">
            <span className="px-2.5 py-1 bg-forge-elevated rounded-md font-mono">MP3</span>
            <span className="px-2.5 py-1 bg-forge-elevated rounded-md font-mono">WAV</span>
            <span>up to {maxSizeMb}MB</span>
          </div>
        </div>
      </label>

      {error && (
        <div className="mt-4 flex items-center gap-2 p-3 bg-[hsl(var(--forge-danger)/0.1)] border border-[hsl(var(--forge-danger)/0.2)] rounded-xl text-[hsl(var(--forge-danger))]">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}
    </div>
  )
}
